import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { parsePNR, formatPNRData } from '../utils/pnrParser';
import { transformPNRToFlightData, validateCustomerData } from '../utils/flightDataTransformer';
import { FlightTicket } from '../utils/requests';

// Wrapper to use navigate hook in class component
function withNavigation(Component) {
  return function WrappedComponent(props) {
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} />;
  };
}

class PNRConverter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // PNR state
      pnrCode: '2 NZ 456 H 26OCT 5 WLGAKL HK1 1945 2050 260CT E NZ/W268NH',
      parsedData: null,
      parseError: null,
      
      // Form state
      showForm: false,
      firstName: '',
      lastName: '',
      email: '',
      subscribeToNewsletter: true,
      formErrors: {},
      
      // Submission state
      isLoading: false,
      submitError: null,
      submitSuccess: false
    };
    
    this.handlePNRChange = this.handlePNRChange.bind(this);
    this.handleFormChange = this.handleFormChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    // Parse initial example PNR
    this.parsePNRCode(this.state.pnrCode);
  }

  handlePNRChange = (event) => {
    const pnrCode = event.target.value;
    this.setState({ pnrCode, showForm: false, submitError: null });
    
    // Debounce parsing
    clearTimeout(this.parseTimeout);
    this.parseTimeout = setTimeout(() => {
      this.parsePNRCode(pnrCode);
    }, 500);
  }

  parsePNRCode = (pnrCode) => {
    if (!pnrCode || pnrCode.trim().length === 0) {
      this.setState({ parsedData: null, parseError: null });
      return;
    }

    const parsed = parsePNR(pnrCode);
    
    if (parsed.error) {
      this.setState({ parsedData: null, parseError: parsed.error });
    } else {
      this.setState({ parsedData: parsed, parseError: null });
    }
  }

  handleFormChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    this.setState({ 
      [field]: value,
      formErrors: { ...this.state.formErrors, [field]: null },
      submitError: null
    });
  }

  handleShowForm = () => {
    this.setState({ showForm: true, submitError: null });
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    
    const { parsedData, firstName, lastName, email, subscribeToNewsletter } = this.state;
    
    // Validate form
    const customerData = {
      first_name: firstName,
      last_name: lastName,
      email: email
    };
    
    const validation = validateCustomerData(customerData);
    if (!validation.valid) {
      this.setState({ formErrors: validation.errors });
      return;
    }
    
    this.setState({ isLoading: true, submitError: null });
    
    try {
      // Transform PNR data to API format (async to fetch city names from airport codes)
      const flightData = await transformPNRToFlightData(parsedData, customerData, subscribeToNewsletter);
      
      // Call the API
      const response = await FlightTicket.orderTicketFree(
        flightData.first_name,
        flightData.last_name,
        flightData.email,
        flightData.airline,
        flightData.arrival_time,
        flightData.city_from,
        flightData.city_to,
        flightData.departure_time,
        flightData.flight_name,
        flightData.fly_duration,
        flightData.fly_from,
        flightData.fly_to,
        flightData.price,
        flightData.route,
        flightData.subscribe_to_newsletter
      );
      
      if (response.status === 200) {
        // Success - navigate to success page
        this.setState({ isLoading: false, submitSuccess: true });
        this.props.navigate('/checkout-success', { state: { email: email } });
      } else if (response.status === 429) {
        // Rate limit
        this.setState({ 
          isLoading: false, 
          submitError: 'You have reached the daily limit for free itineraries (10/day). Please try again tomorrow.'
        });
      } else {
        // Other error
        const errorData = await response.json().catch(() => ({}));
        this.setState({ 
          isLoading: false, 
          submitError: errorData.error || 'Failed to generate itinerary. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error generating itinerary:', error);
      this.setState({ 
        isLoading: false, 
        submitError: 'An error occurred. Please check your connection and try again.'
      });
    }
  }

  renderForm() {
    const { firstName, lastName, email, subscribeToNewsletter, formErrors, isLoading, submitError } = this.state;
    
    return (
      <div className="card shadow-sm mt-4">
        <div className="card-body">
          <h5 className="card-title mb-4">Generate Itinerary</h5>
          
          {submitError && (
            <div className="alert alert-danger" role="alert">
              {submitError}
            </div>
          )}
          
          <form onSubmit={this.handleSubmit}>
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="firstName" className="form-label">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  className={`form-control ${formErrors.first_name ? 'is-invalid' : ''}`}
                  value={firstName}
                  onChange={this.handleFormChange('firstName')}
                  placeholder="John"
                  disabled={isLoading}
                />
                {formErrors.first_name && (
                  <div className="invalid-feedback">{formErrors.first_name}</div>
                )}
              </div>
              <div className="col-md-6">
                <label htmlFor="lastName" className="form-label">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  className={`form-control ${formErrors.last_name ? 'is-invalid' : ''}`}
                  value={lastName}
                  onChange={this.handleFormChange('lastName')}
                  placeholder="Doe"
                  disabled={isLoading}
                />
                {formErrors.last_name && (
                  <div className="invalid-feedback">{formErrors.last_name}</div>
                )}
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                id="email"
                className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                value={email}
                onChange={this.handleFormChange('email')}
                placeholder="your@email.com"
                disabled={isLoading}
              />
              {formErrors.email && (
                <div className="invalid-feedback">{formErrors.email}</div>
              )}
              <div className="form-text">Your itinerary will be sent to this email address.</div>
            </div>
            
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                id="newsletter"
                className="form-check-input"
                checked={subscribeToNewsletter}
                onChange={this.handleFormChange('subscribeToNewsletter')}
                disabled={isLoading}
              />
              <label htmlFor="newsletter" className="form-check-label">
                Subscribe to newsletter for updates
              </label>
            </div>
            
            <button 
              type="submit" 
              className="btn btn-primary btn-lg w-100"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Generating...
                </>
              ) : (
                'Generate & Send Itinerary'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  render() {
    const { pnrCode, parsedData, parseError, showForm } = this.state;
    const formattedData = parsedData ? formatPNRData(parsedData) : null;

    return (
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col">
            <h2>PNR Decoder</h2>
            <p className="text-start">
              Decode and view flight information from PNR (Passenger Name Record) codes. This tool helps you quickly understand flight details from raw booking data.
            </p>
            <p className="text-start">
              Paste your PNR code below and get instant results showing airline, flight number, route, dates, times, and class information in an easy-to-read format.
            </p>
          </div>
        </div>

        <div className="row">
          {/* Left Panel - Input */}
          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="pnr-input" className="form-label"><strong>Enter your PNR code</strong></label>
                  <textarea
                    id="pnr-input"
                    className="form-control font-monospace"
                    rows={6}
                    value={pnrCode}
                    onChange={this.handlePNRChange}
                    placeholder="2 NZ 456 H 26OCT 5 WLGAKL HK1 1945 2050 260CT E NZ/W268NH"
                  />
                  {parseError && (
                    <div className="alert alert-warning mt-2 mb-0" role="alert">
                      {parseError}
                    </div>
                  )}
                  {!parseError && parsedData && (
                    <div className="form-text text-success">
                      ✓ PNR decoded successfully
                    </div>
                  )}
                </div>
                
                {/* Generate Itinerary Button */}
                {parsedData && !parseError && !showForm && (
                  <button 
                    className="btn btn-success btn-lg w-100"
                    onClick={this.handleShowForm}
                  >
                    Generate Itinerary from PNR
                  </button>
                )}
              </div>
            </div>
            
            {/* Inline Form */}
            {showForm && this.renderForm()}
          </div>

          {/* Right Panel - Decoded Information */}
          <div className="col-lg-6">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-4">Decoded Flight Information</h5>
                
                {formattedData && !formattedData.error ? (
                  <div className="p-3 bg-light rounded">
                    <div className="mb-4">
                      <h6 className="text-primary">Flight Details</h6>
                      <table className="table table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td><strong>Airline:</strong></td>
                            <td>{formattedData.airline}</td>
                          </tr>
                          <tr>
                            <td><strong>Flight Number:</strong></td>
                            <td>{formattedData.flightNumber}</td>
                          </tr>
                          <tr>
                            <td><strong>Route:</strong></td>
                            <td>{formattedData.route}</td>
                          </tr>
                          <tr>
                            <td><strong>Departure:</strong></td>
                            <td>{formattedData.departure}</td>
                          </tr>
                          <tr>
                            <td><strong>Arrival:</strong></td>
                            <td>{formattedData.arrival}</td>
                          </tr>
                          <tr>
                            <td><strong>Class:</strong></td>
                            <td>{formattedData.class}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    {parsedData.raw && (
                      <div className="mt-4 pt-3 border-top">
                        <small className="text-muted">
                          <strong>Raw PNR:</strong> {parsedData.raw}
                        </small>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-muted text-center py-5">
                    {parseError ? (
                      <div className="alert alert-warning" role="alert">{parseError}</div>
                    ) : (
                      <div>
                        <p>Enter a PNR code to see the decoded flight information here.</p>
                        <small>Example: 2 NZ 456 H 26OCT 5 WLGAKL HK1 1945 2050 260CT E NZ/W268NH</small>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withNavigation(PNRConverter);
